import React from "react";
import { CCCombobox, CCField, useToast } from "@pymblecitizencoders/cc-ui";
import { useEventTypes } from "../../hooks/useEventTypes";
import { useEventTags } from "../../hooks/useEventTags";
import { EventTagField } from "./EventTagField";
import { GuardedRichText } from "../common/GuardedRichText";
import { EventDoc } from "../../types/event";
import { isEmptyRichText } from "../../utils/richText";
import { supervisorNames } from "../../utils/eventOwner";
import "./eventManager.css";

interface EventDetailsTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * Details tab. pymble-swipe's CreateEventTab pairs a title field with a
 * location select and a rich-text body.
 *
 * Text inputs are cc-ui CCField (floating label, so no separate Form.Label or
 * placeholder). The rich-text editor is cc-ui's CCRichText, from the `/editor`
 * entry point added in cc-ui 0.2.0 — it replaced pymble-ui's TextEditor, which
 * was the only thing this app still imported pymble-ui code for.
 *
 * pymble-swipe's Internal/External event toggle was not reproduced, for want of
 * a field on the S2 document to save it to. The sidebar's "Public" switch is
 * the nearest thing, and it lives there rather than here because it is about
 * who the event is FOR rather than about what it is.
 */
export const EventDetailsTab: React.FC<EventDetailsTabProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const toast = useToast();
	const { types: eventTypes, loaded: typesLoaded } = useEventTypes();
	const { tags: tagCatalogue } = useEventTags();

	/* Memoised on the ROLL, not on the event: the event is the draft, so it is a
	   new object on every keystroke, and this would filter a few hundred
	   attendees each time anyone typed in the title. `Attendees` only changes
	   when the fetch lands. */
	const owners = React.useMemo(
		() => supervisorNames(event.Attendees),
		[event.Attendees]
	);

	return (
		<div>
			<h6 className="em-section-heading">Event details</h6>
			<div className="em-grid">
				<div className="em-col-md-4">
					<CCField
						label="Title"
						type="text"
						fullWidth
						value={event.Title ?? ""}
						disabled={readOnly}
						onChange={(e) => onChange({ Title: e.target.value })}
					/>
				</div>

				<div className="em-col-md-4">
					{/* Beside the title because it classifies the event, which is what
					    the rest of this row does. It does NOT choose the approval
					    workflow, whatever its placement suggests: `_pick_workflow` in
					    pymble-apis matches on the CALENDAR an event is on, through
					    `derive_category`, and the `AppliesTo.EventTypes` it reads is
					    that set of calendar labels rather than these ten.

					    EMPTY STRING, NOT NULL, when nothing is chosen. The API tells
					    "not sent" from "cleared" by null versus empty — v2 omits this
					    field entirely, so `_optional_update_fields` has to skip a null
					    rather than blank the type on every save from that app. Send
					    null here and a type can be set but never unset, silently and
					    with a 200. `_check_val` turns the empty string back into a
					    stored null at the other end.

					    `|| null` on the way IN is the mirror of it: a cleared value
					    must read to the combobox as no selection, not as an option
					    that is not in the list. */}
					<CCCombobox
						label="Event type"
						fullWidth
						disabled={readOnly}
						/* From the dictionary now, not a constant here. The API routes
						   approvals on this value and validates it against the same
						   catalogue, so a second list in the client could offer a type
						   the server refuses. */
						options={eventTypes.map((t) => ({
							value: t.Code,
							label: t.Name ?? t.Code,
						}))}
						hint={
							typesLoaded && eventTypes.length === 0
								? "No event types are configured yet."
								: undefined
						}
						value={event.EventType || null}
						onChange={(value) => onChange({ EventType: value || "" })}
					/>
				</div>

				{/* READ-ONLY, and deliberately not disabled. It is not a locked
				    editable field — there is nothing on the event document to
				    save an owner to, and the people here are a consequence of
				    who is linked to the event on the Attendees tab. `readOnly`
				    keeps full contrast and lets the name be selected and
				    copied, which is most of what anyone wants from it; the
				    greyed-out look of `disabled` would promise an edit that
				    unlocking the form does not deliver.

				    Ends the first row, with the facts that identify the event
				    rather than among the things written about it. */}
				<div className="em-col-md-4">
					<CCField
						label="Event owner"
						type="text"
						fullWidth
						readOnly
						value={owners[0] ?? ""}
						/* Only says when there is more than one. A single owner, or
						   none, needs no caption. */
						hint={
							owners.length > 1
								? `and ${owners.length - 1} other supervisor${
										owners.length > 2 ? "s" : ""
									}`
								: undefined
						}
					/>
				</div>

				<div>
					<CCField
						label="Short description"
						multiline
						/* One row: it is a one-line summary, and two thirds of the
						   grid is ample room per line. Kept as a textarea rather
						   than a single-line input so a longer summary still wraps
						   and scrolls instead of scrolling sideways out of view.

						   md-8 rather than the full 12 it had before the owner
						   field: the two together fill one row, where the owner on
						   its own would have left two thirds of a row empty.

						   em-field-compact is required for `rows` to take effect at
						   all — cc-ui floors textareas at 4.5em. */
						className="em-field-compact"
						rows={1}
						fullWidth
						value={event.Description?.Short ?? ""}
						disabled={readOnly}
						onChange={(e) =>
							onChange({
								Description: {
									...(event.Description ?? {}),
									Short: e.target.value,
								},
							})
						}
					/>
				</div>

				{/* The two URLs are a pair, so they share a row of their own rather
				    than being stacked in a column beside the description. md, so
				    they drop one above the other before they get too narrow to
				    read a URL in. */}
				<div className="em-col-md-6">
					<CCField
						label="Event URL"
						type="url"
						fullWidth
						value={event.Approval?.Urls?.Event ?? ""}
						disabled={readOnly}
						onChange={(e) =>
							onChange({
								Approval: {
									...(event.Approval ?? {}),
									Urls: {
										...(event.Approval?.Urls ?? {}),
										Event: e.target.value,
									},
								},
							})
						}
					/>
				</div>

				<div className="em-col-md-6">
					<CCField
						label="More info URL"
						type="url"
						fullWidth
						value={event.Approval?.Urls?.MoreInfo ?? ""}
						disabled={readOnly}
						onChange={(e) =>
							onChange({
								Approval: {
									...(event.Approval ?? {}),
									Urls: {
										...(event.Approval?.Urls ?? {}),
										MoreInfo: e.target.value,
									},
								},
							})
						}
					/>
				</div>

				<div>
					<label className="em-label">Event notes</label>
					{/* `Description.Long`, NOT `AdditionalNotes`.

					    The long description is the field the event's notes actually
					    live in, and it is what the portal renders under the event's
					    description; event-manager-v2 pointed this same editor at
					    `AdditionalNotes` while labelling it "Description", which is
					    where the confusion between the two started.

					    `AdditionalNotes` is left alone rather than cleared: the save
					    sends the whole loaded document, so whatever an event already
					    holds there survives untouched — it simply has no editor here
					    any more. The portal still renders it beneath this.

					    CCRichText supports `editable`, so read-only mode reuses the
					    same component rather than rendering raw HTML — no
					    dangerouslySetInnerHTML, no XSS surface.

					    No `onUploadImage` is wired: the S2 event document has no
					    image store behind it, and CCRichText refuses dropped images
					    rather than inlining them as base64 (which would bloat the
					    saved payload). The refusal is surfaced as a toast so a
					    dropped photo does not just vanish silently. */}
					{/* GuardedRichText, not CCRichText. TipTap re-serialises whatever
					    it is given, so opening an event with notes emitted a
					    normalised copy of them and opening one without emitted
					    `<p></p>` — both indistinguishable from an edit, and both
					    marked the event unsaved before anyone touched it. The guard
					    drops anything emitted while the editor is not focused. */}
					<GuardedRichText
						className="em-rte-capped"
						value={event.Description?.Long ?? ""}
						editable={!readOnly}
						placeholder="Notes shown to parents alongside the event"
						/* Emptiness is stored as null, so that is what an emptied
						   editor writes back. */
						onChange={(html: string) =>
							onChange({
								Description: {
									...(event.Description ?? {}),
									/* An empty string, not null: the API skips a null and so
									   would keep the old text; it stores a blank as null. */
									Long: isEmptyRichText(html) ? "" : html,
								},
							})
						}
						onImageRefused={() =>
							toast.show({
								message:
									"Images cannot be added to event notes. Link to the image instead.",
								severity: "warning",
								duration: 5000,
							})
						}
					/>
				</div>

				<div>
					<label className="em-label">Additional notes</label>
					{/* `AdditionalNotes`, the second notes field. The portal renders
					    it beneath the event notes above, and the permission card
					    preview draws it, so it needs an editor for the preview to
					    mean anything. Same guarded editor and the same
					    emptiness-is-null rule as the notes above; the API writes this
					    field unconditionally, so null clears it. */}
					<GuardedRichText
						className="em-rte-capped"
						value={event.AdditionalNotes ?? ""}
						editable={!readOnly}
						placeholder="Extra notes shown beneath the event notes"
						onChange={(html: string) =>
							onChange({
								AdditionalNotes: isEmptyRichText(html) ? null : html,
							})
						}
						onImageRefused={() =>
							toast.show({
								message:
									"Images cannot be added to event notes. Link to the image instead.",
								severity: "warning",
								duration: 5000,
							})
						}
					/>
				</div>

				{/* LAST of the editable fields, after the notes. Tags are filing
				    rather than content: nothing downstream reads them, they choose
				    no workflow and gate no approval, and they are how the event is
				    found again once it exists. So they sit below the things that
				    describe it rather than interrupting the run from title to
				    notes.

				    Still ahead of the provenance line, which is not a field at all.

				    FULL WIDTH. It was half, back when the chips sat above the box
				    and the field was only as tall as one control. With the chips
				    beside the box, half a row left them about 150px to sit in and
				    three tags wrapped onto a second line — so the field was taller
				    at half width than it is at full, and the space to its right was
				    empty the whole time. The box does not grow to fill this; it
				    keeps a fixed width and the tags take everything left over. */}
				<div>
					<EventTagField
						values={event.Tags ?? []}
						catalogue={tagCatalogue}
						disabled={readOnly}
						/* No catalogue error state. A failed dictionary read costs the
						   type-ahead its suggestions and nothing else: the field still
						   accepts anything, and the tags already on the event render
						   from the document rather than from the catalogue. */
						onChange={(Tags) => onChange({ Tags })}
					/>
				</div>

			</div>
		</div>
	);
};
